import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SearchOutlined,
  GoogleOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useUsers, useDeleteUser } from "~/services/user";
import { TUser } from "~/types";
import { METADATA_DEFAULT, Role } from "~/utils/constants";
import { CreateUserForm } from "../admin-management/create-user-form";
import { EditUserForm } from "../admin-management/edit-user-form";
import { useAuth } from "~/store";
import { MetaData } from "~/types/common";

const { Title } = Typography;
const { Option } = Select;

export const UserManagement: React.FC = () => {
  const rolesDefault = [Role.SUPER_ADMIN, Role.ADMIN, Role.LEAD, Role.STAFF, Role.CLIENT] as Role[];
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [rolesFilter, setRolesFilter] = useState<Role[]>(rolesDefault);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TUser | null>(null);
  const [meta, setMeta] = useState<MetaData>(METADATA_DEFAULT);

  // Fetch users
  const {
    data: usersData,
    isLoading,
    refetch,
  } = useUsers({
    page: currentPage,
    pageSize,
    search,
    roles: rolesFilter,
  });

  useEffect(() => {
    if (usersData?.meta && usersData.meta.totalItems !== meta.totalItems) {
      setMeta(usersData?.meta);
    }
  }, [usersData?.meta]);

  // const deleteUserMutation = useDeleteUser();

  // Only show if current user is SUPER_ADMIN
  if (currentUser?.role !== Role.SUPER_ADMIN) {
    return (
      <Card>
        <div className="text-center py-8">
          <UserOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} className="text-gray-500">
            You don't have permission to access this page
          </Title>
        </div>
      </Card>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "red";
      case Role.ADMIN:
        return "blue";
      case Role.LEAD:
        return "orange";
      case Role.STAFF:
        return "green";
      case Role.CLIENT:
        return "default";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "Super Admin";
      case Role.ADMIN:
        return "Admin";
      case Role.LEAD:
        return "Lead";
      case Role.STAFF:
        return "Staff";
      case Role.CLIENT:
        return "Client";
      default:
        return role;
    }
  };

  const columns: ColumnsType<TUser> = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (username, record) => (
        <Space>
          <UserOutlined />
          <span>
            {record.firstName && record.lastName
              ? `${record.firstName} ${record.lastName}`
              : username}
          </span>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phone) => phone || "-",
    },
    {
      title: "Google",
      key: "google",
      width: 80,
      align: "center" as const,
      render: (_, record) =>
        record.googleId ? (
          <Tag icon={<GoogleOutlined />} color="blue">
            {record.googleEmail || "Linked"}
          </Tag>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
      filters: [
        { text: "Super Admin", value: Role.SUPER_ADMIN },
        { text: "Admin", value: Role.ADMIN },
        { text: "Lead", value: Role.LEAD },
        { text: "Staff", value: Role.STAFF },
        { text: "Client", value: Role.CLIENT },
      ],
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("en-US"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedUser(record);
              setIsEditModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => {
              console.log("hanle delete user", record.id);
              // deleteUserMutation.mutate(record.id)
            }}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={record.id === currentUser?.id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any, filters: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setRoleFilter(filters.role?.[0]);
  };

  const handleFilterRole = (value: string) => {
    setRoleFilter(value);
    setRolesFilter([value as Role]);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setRoleFilter(undefined);
    setRolesFilter(rolesDefault);
    setSearch("");
    setCurrentPage(1);
    refetch()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} className="mb-0">
              User Management
            </Title>
            <p className="text-gray-500 mt-2">
              Manage user accounts in the system
            </p>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
            >
              Create New User
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Filters */}
      <Card>
        <Row gutter={16}>
          <Col span={12}>
            <Input
              placeholder="Search by name, email..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by role"
              style={{ width: "100%" }}
              value={roleFilter}
              onChange={handleFilterRole}
              allowClear
            >
              <Option value={Role.SUPER_ADMIN}>Super Admin</Option>
              <Option value={Role.ADMIN}>Admin</Option>
              <Option value={Role.LEAD}>Lead</Option>
              <Option value={Role.STAFF}>Staff</Option>
              <Option value={Role.CLIENT}>Client</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button onClick={() => handleReset()}>Refresh</Button>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={usersData?.items}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: meta?.totalItems,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={600}
      >
        <CreateUserForm
          onSuccess={() => {
            setIsCreateModalVisible(false);
            refetch();
          }}
          onCancel={() => setIsCreateModalVisible(false)}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <EditUserForm
            user={selectedUser}
            onSuccess={() => {
              setIsEditModalVisible(false);
              setSelectedUser(null);
              refetch();
            }}
            onCancel={() => {
              setIsEditModalVisible(false);
              setSelectedUser(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};
